/**
 * The closed set of AI features a practice may opt into, and the request/response shapes for
 * `GET`/`PUT /api/org/ai-settings` (`BACKLOG.md` C20).
 *
 * **Deliberately separate from `entitlements.ts`'s `Feature`/`isEntitled`, not an extension of it.**
 * `isEntitled` answers "does this practice's *plan* include X?" — tier-based commercial packaging,
 * or (for `NEVER_GATED`) a certified capability that must never be tier-gated at all (T9/T13's
 * never-gate rule). An AI feature toggle is neither: it is an ordinary per-org admin *opt-in* switch,
 * available identically on every tier, that a practice controls for itself because it has chosen (or
 * not) to let an AI feature run for its own staff — a liability/consent decision, not a packaging one.
 * `emr-po`'s C6 settles this posture: "AI usage is admin-gated per practice (a practice opts into
 * which AI features it wants at the admin/tenant level)... ordinary commercial/config gating, not a
 * certified-capability gate." Nothing here goes near `NEVER_GATED` or `CERTIFIED_MODULE_CAPABILITIES`.
 *
 * Extend `AI_FEATURES` as real AI features ship. `helpChat` (`BACKLOG.md` B6a) is the only one today.
 */

import { z } from 'zod';

export const AI_FEATURES = ['helpChat'] as const;
export type AiFeature = (typeof AI_FEATURES)[number];

export function isAiFeature(value: string): value is AiFeature {
  return (AI_FEATURES as readonly string[]).includes(value);
}

export const aiSettingSchema = z.strictObject({
  feature: z.enum(AI_FEATURES),
  enabled: z.boolean(),
});
export type AiSetting = z.infer<typeof aiSettingSchema>;

/** `GET /api/org/ai-settings` response: current state for every known AI feature, defaulting to
 * `enabled: false` for anything the org has never opted into (`server/src/ai/orgAiSettings.ts`'s own
 * store contract) — the response always lists every member of `AI_FEATURES`, never only the ones a
 * practice has touched, so the web app never has to guess what "absent" means. */
export const aiSettingsResponseSchema = z.strictObject({
  settings: z.array(aiSettingSchema),
});
export type AiSettingsResponse = z.infer<typeof aiSettingsResponseSchema>;

export const updateAiSettingRequestSchema = z.strictObject({
  enabled: z.boolean(),
});
export type UpdateAiSettingRequest = z.infer<typeof updateAiSettingRequestSchema>;

export const updateAiSettingResponseSchema = aiSettingSchema;
export type UpdateAiSettingResponse = z.infer<typeof updateAiSettingResponseSchema>;

export const AI_SETTINGS_ERROR_CODES = ['invalid_request', 'audit_unavailable'] as const;
export type AiSettingsErrorCode = (typeof AI_SETTINGS_ERROR_CODES)[number];
